import { KEY_CODE_SIHEUNG } from '@/constants/app';
import { CategoryType } from '@/enums/floor-guide';
import type { languageType } from '@/stores';
import { FOCUS_TYPE, LANG_TYPE } from 'dabeeomaps';

export const NUMBER_OF_FLOORS_PER_VIEW_VERTICAL = 9;

// Floor list constants
export const FLOOR_LIST_CONFIG = {
    FULLSCREEN: {
        SLIDES_PER_VIEW: 7,
        SLIDES_PER_GROUP: 7,
        AMOUNT_TO_SHOW: 7,
    },
    NORMAL: {
        SLIDES_PER_VIEW: 7,
        SLIDES_PER_GROUP: 7,
        AMOUNT_TO_SHOW: 7,
    },
} as const;

// zoom level constants for map
export const MAP_ZOOM_LEVEL = {
    MIN: 19,
    MAX: 24,
};

export const ZOOM_LEVEL_STEP = 0.5;

export const DEFAULT_ZOOM_LEVEL = MAP_ZOOM_LEVEL.MIN;

export const MY_LOCATION_MARKER = {
    BASE_SIZE: { width: 130, height: 234 },
    PIN_TIP_Y: 117,
    PULSE_SIZE: 65,
    PULSE_CENTER_Y: 117,
    REFERENCE_ZOOM: 22,
    MIN_VISIBLE_PX: 52,
} as const;

export const LEGACY_MY_LOCATION_MARKER = {
    BASE_SIZE: { width: 124, height: 124 },
    REFERENCE_ZOOM: 22,
    MIN_VISIBLE_PX: 52,
} as const;

export const MY_LOCATION_ANIMATE = {
    COLOR: '#EC4545',
    OPACITY: 0.3,
    DESIRE_SCALE: 1.5,
    DURATION: 1200,
    REPEAT: 2,
} as const;

export const MY_LOCATION_ACTIVE_ID = 'mylocation-active';

export const MY_LOCATION_ZOOM_UPDATE_DEBOUNCE_MS = 200;

export const isSiheungVerticalMyLocationEnabled = (storeCode: string, isHorizontal: boolean): boolean => {
    return KEY_CODE_SIHEUNG.includes(storeCode) && !isHorizontal;
};

export const MAP_CATEGORY_CLIENT_CODE = 'L001';
export const MAP_CATEGORY_INDENT_LEVEL = 2;

export const DEFAULT_ROUTE_SIMULATION_PARAMS = {
    SPEED: 10,
    LINE_WEIGHT: 13,
};

export const ROUTE_DIRECTION_TEXT = {
    GO_STRAIGHT: '직진',
    TURN_RIGHT: '우회전',
    TURN_LEFT: '좌회전',
    U_TURN: '유턴',
};

export const TRANSPORTATION_TEXT = {
    WALKING: '걷기',
    TOW_WAYS_ESCALATOR: '에스컬레이터 양방향',
    ELEVATOR: '엘리베이터',
    ESCALATOR_UP: '에스컬레이터 상행',
    ESCALATOR_DOWN: '에스컬레이터 하행',
    STAIRS: '계단',
    OTHER_TRANSPORT: '기타이동수단',
};

export const CATEGORY_CODE_ID = {
    [CategoryType.BABY_LOUNGE]: 'S008',
    [CategoryType.CUSTOMER_SERVICE_CENTER]: 'S009',
    [CategoryType.ELEVATOR]: 'S001',
    [CategoryType.ESCALATOR]: 'S003',
    [CategoryType.EXCHANGE]: 'S010',
    [CategoryType.GIFT_REDEMPTION]: 'S006',
    [CategoryType.INFORMATION]: 'S004',
    [CategoryType.LOCKER]: 'S005',
    [CategoryType.RESTROOM]: 'S002',
    [CategoryType.STROLLER_RENTAL]: 'S007',
    [CategoryType.TAX_REFUND]: 'S011',
};

export const CATEGORY_ORDER = {
    [CATEGORY_CODE_ID[CategoryType.BABY_LOUNGE]]: 1,
    [CATEGORY_CODE_ID[CategoryType.CUSTOMER_SERVICE_CENTER]]: 2,
    [CATEGORY_CODE_ID[CategoryType.TAX_REFUND]]: 3,
    [CATEGORY_CODE_ID[CategoryType.EXCHANGE]]: 4,
    [CATEGORY_CODE_ID[CategoryType.STROLLER_RENTAL]]: 5,
    [CATEGORY_CODE_ID[CategoryType.GIFT_REDEMPTION]]: 6,
    [CATEGORY_CODE_ID[CategoryType.LOCKER]]: 7,
    [CATEGORY_CODE_ID[CategoryType.INFORMATION]]: 8,
    [CATEGORY_CODE_ID[CategoryType.ESCALATOR]]: 9,
    [CATEGORY_CODE_ID[CategoryType.ELEVATOR]]: 10,
    [CATEGORY_CODE_ID[CategoryType.RESTROOM]]: 11,
};

export const OBJECT_STYLE_SET = {
    unit: {
        color: '#ffffff',
        stroke: '#373d41',
    },
    detail: {
        color: '#5A5652',
        stroke: '#000000',
    },
    fixture: {
        color: '#7A7A7A',
        stroke: '#000000',
    },
    section: {
        color: '#44403C',
        stroke: '#000000',
    },
    footprint: {
        color: '#6E6E6E',
        stroke: '#000000',
    },
    level: {
        color: '#f2f2f3',
        stroke: '#f2f2f3',
    },
};

export const OBJECT_GROUP = {
    unit: [],
    detail: [],
    fixture: [],
    section: [],
    footprint: [],
    level: [],
};

export const FOCUS_TO_MAX_ZOOM = 21;

export const FOCUS_TO_OPTIONS = {
    focus: {
        type: FOCUS_TYPE.NAVIGATION,
    },
    transition: true,
    maxZoom: FOCUS_TO_MAX_ZOOM,
    padding: {
        top: 20,
        left: 100,
        bottom: 20,
        right: 20,
    },
};

export const MAP_CONTRAST_COLOR = {
    POI: {
        innerColor: '#373d41',
        outerColor: '#373d41',
    },
};

export const FOCUS_TO_OBJECT_PADDING = {
    SINGLE_OBJECT: {
        top: 200,
        left: 200,
        bottom: 200,
        right: 200,
    },
    MULTI_OBJECT: {
        top: 50,
        left: 50,
        bottom: 50,
        right: 50,
    },
};

export const MAPPING_APP_LANGUAGE_TO_MAP_LANGUAGE = {
    ko: LANG_TYPE.KO,
    en: LANG_TYPE.EN,
    ja: LANG_TYPE.JA,
    'zh-CN': LANG_TYPE.ZH_CN,
    'zh-TW': 'zh-TW',
} as const;

export const MAPPING_MENU_LANGUAGE: Record<languageType, string> = {
    ko: 'shopPrdcNm',
    en: 'shopPrdcEngNm',
    ja: 'shopPrdcJpnNm',
    'zh-CN': 'shopPrdcChsNm',
    'zh-TW': 'shopPrdcChtNm',
};

export const POI_ID_DELIMITER = '__poi_id__';

export const ZOOM_SCROLL_CONTAINER_SELECTOR = '[data-zoom-scroll-container]';
